import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  SelectChangeEvent,
  TextField,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Checkbox,
} from "@mui/material";
import { ImageUploaderModalProps } from "types";
import { getMediaList } from "services/apiService";
import ImageUpload from "./ImageUpload";
import { FixedSizeGrid as Grid } from "react-window";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";

const ImageUploaderModal: React.FC<ImageUploaderModalProps> = ({
  isOpen,
  onClose,
  onImageSelect,
  allowMultiple = false,
}) => {
  const [imageGallery, setImageGallery] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [filterType, setFilterType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const baseUrl = process.env.REACT_APP_IMAGE_URL || "";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const fetchAllImages = useCallback(async () => {
    setIsLoading(true);
    const response = await getMediaList();
    setImageGallery(response?.data);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchAllImages();
    }
  }, [isOpen, fetchAllImages, activeTab]);

  const handleImageUploadSuccess = useCallback(() => {
    fetchAllImages();
  }, [fetchAllImages]);

  const handleCheckboxChange = (imgName: string) => {
    setSelectedImages((prevSelected) => {
      if (allowMultiple) {
        if (prevSelected?.includes(imgName)) {
          return prevSelected.filter((name) => name !== imgName);
        } else {
          return [...prevSelected, imgName];
        }
      } else {
        if (prevSelected?.includes(imgName)) {
          return prevSelected;
        } else {
          onImageSelect([imgName]);
        }
      }
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
  };

  const handleConfirmSelection = () => {
    onImageSelect(selectedImages);
    setSelectedImages([]);
    onClose();
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilterType(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredImages = imageGallery.filter((img) => {
    const extension = img.split(".").pop()?.toLowerCase();
    const fileName = img.split("/").pop()?.toLowerCase() || "";

    if (extension === "mp4") return false;
    if (filterType !== "all" && `.${extension}` !== filterType) return false;
    if (searchQuery && !fileName.includes(searchQuery.toLowerCase()))
      return false;

    return true;
  });

  const cellRenderer = ({ columnIndex, rowIndex, style }: any) => {
    const imageIndex = rowIndex * 6 + columnIndex;
    const img = filteredImages[imageIndex];

    return img ? (
      <Box
        style={style}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <Card
          sx={{
            width: 170,
            textAlign: "center",
            borderRadius: "5px",
            overflow: "hidden",
            transition: "transform 0.2s ease-in-out",
            "&:hover": { transform: "scale(1.05)" },
            position: "relative",
            boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          <Checkbox
            checked={selectedImages?.includes(img)}
            onChange={() => handleCheckboxChange(img)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(255,255,255,0.8)",
              borderRadius: "50%",
              padding: "2px",
            }}
          />

          <CardMedia
            component="img"
            image={`${baseUrl}${img}`}
            alt={`image-${rowIndex}-${columnIndex}`}
            sx={{
              width: "100%",
              height: "160px",
              objectFit: "contain",
              backgroundColor: "#f5f5f5",
            }}
          />

          <CardContent
            sx={{
              backgroundColor: "#e4e4e4",
              padding: "10px",
              paddingBottom: "5px !important",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "10px",
                color: "#333",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {(() => {
                const fileName = img.split("/").pop() || "";
                return fileName.length > 15
                  ? `${fileName.slice(0, 15)}...`
                  : fileName;
              })()}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    ) : null;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} fullWidth maxWidth="lg">
      <DialogTitle>
        <Button
          variant="outlined"
          color="secondary"
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            border: "none",
            fontSize: "20px",
          }}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </Button>
      </DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab label="Gallery" />
          <Tab label="Uploader" />
        </Tabs>
        {activeTab === 0 && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={2}
            >
              <FormControl
                variant="outlined"
                size="small"
                sx={{ width: "200px" }}
              >
                <InputLabel id="filter-label">Filter by Type</InputLabel>
                <Select
                  labelId="filter-label"
                  value={filterType}
                  onChange={handleFilterChange}
                  label="Filter by Type"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value=".jpg">JPG</MenuItem>
                  <MenuItem value=".png">PNG</MenuItem>
                  <MenuItem value=".gif">GIF</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                variant="outlined"
                placeholder="Search Image..."
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ width: "250px" }}
              />

              {selectedImages?.length > 0 && (
                <Typography variant="body2" sx={{ color: "grey" }}>
                  Items Selected ({selectedImages?.length})
                </Typography>
              )}
            </Box>

            <Box ref={gridContainerRef} sx={{ height: 600, width: "100%" }}>
              {isLoading ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={450}
                  animation="wave"
                />
              ) : (
                <Grid
                  columnWidth={188}
                  rowHeight={230}
                  columnCount={6}
                  rowCount={Math.ceil(filteredImages.length / 6)}
                  width={gridContainerRef.current?.clientWidth || 0}
                  height={580}
                >
                  {cellRenderer}
                </Grid>
              )}
            </Box>

            {allowMultiple && (
              <DialogActions>
                <Button onClick={onClose} color="error">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmSelection}
                  variant="contained"
                  color="primary"
                  disabled={selectedImages?.length === 0}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            )}
          </>
        )}

        {activeTab === 1 && (
          <ImageUpload onUploadSuccess={handleImageUploadSuccess} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageUploaderModal;
